import React, { useState, useEffect } from 'react';
import { adminService } from '../../firebase/adminService';
import { Plus, Table, AlertTriangle } from 'lucide-react';
import { emailService } from '../../firebase/emailService';
import { timeToMinutes, minutesToTime, RESERVATION_DURATION } from '../../firebase/tableService';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import ScheduleExceptions from '../../components/ScheduleExceptions';

const Reservations = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [availableTables, setAvailableTables] = useState([]);
  const [showOccupyModal, setShowOccupyModal] = useState(false);
  const [tableSuggestions, setTableSuggestions] = useState([]);
  const [occupyTableWarning, setOccupyTableWarning] = useState("");
  const [tableWarning, setTableWarning] = useState("");
  const [activeTab, setActiveTab] = useState('all');
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedComments, setSelectedComments] = useState('');
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [activeAdminTab, setActiveAdminTab] = useState('reservations');
  const [occupyData, setOccupyData] = useState({
    time: '',
    guests: '',
    selectedTables: [],
    status: 'SENTADA'
  });
  const [newReservation, setNewReservation] = useState({
    date: selectedDate,
    time: '',
    guests: '',
    customer: {
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    },
    selectedTables: [],
    isPublic: false,
    status: 'PENDIENTE DE CONFIRMACION'
  });

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const openCommentsModal = (reservation) => {
    setSelectedComments(reservation.customer.comments || 'Sin comentarios');
    setSelectedCustomerName(`${reservation.customer.firstName} ${reservation.customer.lastName}`);
    setShowCommentsModal(true);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reservationsData, tablesData] = await Promise.all([
        adminService.getReservationsByDate(selectedDate),
        adminService.tables.getAll()
      ]);
      
      setReservations(reservationsData);
      setAvailableTables(tablesData);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Calcular mesas necesarias basado en el número de comensales
  const calculateRequiredTables = (guests, availableTablesToUse) => {
    // Si no hay mesas disponibles o no es un array válido, calcular con capacidad estándar
    if (!availableTablesToUse || !Array.isArray(availableTablesToUse) || availableTablesToUse.length === 0) {
      return Math.ceil(parseInt(guests) / 3); // Cálculo tradicional
    }
    
    // Ordenamos las mesas por capacidad (mayor a menor)
    const sortedTables = [...availableTablesToUse].sort((a, b) => 
      (b.capacity || 3) - (a.capacity || 3)
    );
    
    let remainingGuests = parseInt(guests);
    let tablesCount = 0;
    let i = 0;
    
    // Simulamos asignar las mesas en orden de mayor a menor capacidad
    while (remainingGuests > 0 && i < sortedTables.length) {
      const tableCapacity = sortedTables[i].capacity || 3;
      remainingGuests -= tableCapacity;
      tablesCount++;
      i++;
    }
    
    // Si todavía quedan invitados sin mesa, necesitamos más mesas
    if (remainingGuests > 0) {
      // Calcular mesas adicionales necesarias con capacidad estándar de 3
      tablesCount += Math.ceil(remainingGuests / 3);
    }
    
    return tablesCount;
  };

  // Función para verificar si tenemos suficiente capacidad
  const hasEnoughCapacity = (selectedTableIds, guestCount, allTables) => {
    if (!selectedTableIds || !Array.isArray(selectedTableIds) || selectedTableIds.length === 0) {
      return false;
    }
    
    // Calcular la capacidad total de las mesas seleccionadas
    const totalCapacity = selectedTableIds.reduce((sum, tableId) => {
      const table = allTables.find(t => t.id === tableId);
      return sum + (table ? (table.capacity || 3) : 0);
    }, 0);
    
    return totalCapacity >= parseInt(guestCount || 0);
  };

  // Agrupar mesas por tipo y disponibilidad
  const getGroupedTables = (time) => {
    if (!time) return { publico: [], temporal: [] };
    
    // Filtrar mesas por disponibilidad en el horario seleccionado
    const filteredTables = availableTables.filter(table => {
      // Si la mesa tiene información de disponibilidad por hora
      if (table.availabilityByHour && table.availabilityByHour[time]) {
        return table.availabilityByHour[time];
      }
      // Si no tiene información específica, verificar estado general
      return table.status === 'available';
    });
    
    // Agrupar por tipo
    const grouped = {
      publico: filteredTables.filter(t => t.type === 'publico'),
      temporal: filteredTables.filter(t => t.type === 'temporal')
    };
    
    return grouped;
  };

  // Función para iniciar la edición
  const handleEdit = (reservation) => {
    setEditingReservation(reservation);
    setNewReservation({
      ...reservation,
      selectedTables: reservation.tables || [reservation.tableId].filter(Boolean),
      date: new Date(reservation.date.split('/').reverse().join('-')).toISOString().split('T')[0]
    });
    setShowAddModal(true);
    
    // Limpiar cualquier advertencia previa
    setTableWarning("");
  };

  // Manejar cambio en número de comensales
  const handleGuestsChange = async (e) => {
    const guests = e.target.value;
    
    setNewReservation(prev => ({
      ...prev,
      guests
    }));

    // Sugerir mesas automáticamente si tenemos tiempo y date
    if (guests && newReservation.time && newReservation.date) {
      try {
        const formattedDate = new Date(newReservation.date).toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        
        // Intentar encontrar mesas disponibles automáticamente
        const suggestedTables = await adminService.autoAssignTables(
          formattedDate,
          newReservation.time,
          guests,
          editingReservation ? [editingReservation.id] : []
        );
        
        // Guardamos las sugerencias
        setTableSuggestions(suggestedTables);
        
        // Verificar si hay suficientes mesas disponibles
        if (suggestedTables.length === 0) {
          setTableWarning("No hay mesas disponibles para este horario. Intente con otro horario o fecha.");
        } else {
          // Ver si las mesas tienen capacidad suficiente
          const totalCapacity = suggestedTables.reduce((sum, tableId) => {
            const table = availableTables.find(t => t.id === tableId);
            return sum + (table ? (table.capacity || 3) : 0);
          }, 0);
          
          if (totalCapacity < guests) {
            setTableWarning(`Las mesas disponibles solo tienen capacidad para ${totalCapacity} personas de ${guests} solicitadas.`);
          } else {
            setTableWarning("");
            
            // Asignar automáticamente las mesas sugeridas
            setNewReservation(prev => ({
              ...prev,
              selectedTables: suggestedTables
            }));
          }
        }
      } catch (error) {
        console.error("Error al sugerir mesas:", error);
        setTableWarning("Error al verificar disponibilidad de mesas.");
      }
    }
  };

  // Manejar cambio en el horario de reserva
  const handleTimeChange = async (e) => {
    const time = e.target.value;
    
    setNewReservation(prev => ({
      ...prev,
      time,
      selectedTables: [] // Resetear selección de mesas al cambiar hora
    }));
    
    // Limpiar cualquier advertencia previa
    setTableWarning("");
    
    // Sugerir mesas automáticamente si tenemos comensales
    if (time && newReservation.guests && newReservation.date) {
      try {
        const formattedDate = new Date(newReservation.date).toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        
        // Intentar encontrar mesas disponibles automáticamente
        const suggestedTables = await adminService.autoAssignTables(
          formattedDate,
          time,
          newReservation.guests,
          editingReservation ? [editingReservation.id] : []
        );
        
        // Guardamos las sugerencias
        setTableSuggestions(suggestedTables);
        
        // Verificar si hay suficientes mesas disponibles
        if (suggestedTables.length === 0) {
          setTableWarning("No hay mesas disponibles para este horario. Intente con otro horario o fecha.");
        } else {
          // Ver si las mesas tienen capacidad suficiente
          const totalCapacity = suggestedTables.reduce((sum, tableId) => {
            const table = availableTables.find(t => t.id === tableId);
            return sum + (table ? (table.capacity || 3) : 0);
          }, 0);
          
          if (totalCapacity < newReservation.guests) {
            setTableWarning(`Las mesas disponibles solo tienen capacidad para ${totalCapacity} personas de ${newReservation.guests} solicitadas.`);
          } else {
            setTableWarning("");
            
            // Asignar automáticamente las mesas sugeridas
            setNewReservation(prev => ({
              ...prev,
              selectedTables: suggestedTables
            }));
          }
        }
      } catch (error) {
        console.error("Error al sugerir mesas:", error);
        setTableWarning("Error al verificar disponibilidad de mesas.");
      }
    }
  };

  // Manejar ocupación en sala
  const handleOccupyTable = async (e) => {
    e.preventDefault();
    try {
      // Validar que tengamos todos los datos necesarios
      if (!occupyData.time || !occupyData.guests || occupyData.selectedTables.length === 0) {
        throw new Error('Por favor, rellena todos los campos');
      }
  
      // Verificar si tenemos suficiente capacidad
      if (!hasEnoughCapacity(occupyData.selectedTables, occupyData.guests, availableTables)) {
        throw new Error(`No hay suficiente capacidad para ${occupyData.guests} comensales con las mesas seleccionadas`);
      }
      
      // Verificar disponibilidad real de las mesas
      const formattedDate = new Date(selectedDate).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      // Verificar cada mesa individualmente
      const tableAvailabilityPromises = occupyData.selectedTables.map(tableId =>
        adminService.checkTableAvailability(
          tableId,
          formattedDate,
          occupyData.time
        )
      );
      
      const tableAvailability = await Promise.all(tableAvailabilityPromises);
      
      // Si alguna mesa no está disponible, mostrar error
      if (!tableAvailability.every(isAvailable => isAvailable)) {
        const unavailableTables = occupyData.selectedTables
          .filter((_, index) => !tableAvailability[index])
          .map(tableId => {
            const table = availableTables.find(t => t.id === tableId);
            return table ? table.name : tableId;
          })
          .join(', ');
        
        throw new Error(`Las siguientes mesas no están disponibles en este horario: ${unavailableTables}`);
      }
  
      const occupationData = {
        date: selectedDate,
        time: occupyData.time,
        guests: parseInt(occupyData.guests),
        tables: occupyData.selectedTables,
        status: 'SENTADA',
        source: 'walk-in',
        customer: {
          firstName: 'Cliente',
          lastName: 'Walk-in',
          email: 'no-email@example.com',
          phone: 'No disponible'
        }
      };
  
      await adminService.createReservation(occupationData);
      await fetchData();
      setShowOccupyModal(false);
      setOccupyData({
        time: '',
        guests: '',
        selectedTables: [],
        status: 'SENTADA'
      });
    } catch (err) {
      setError(err.message || 'Error al ocupar la mesa');
      console.error('Error completo:', err);
    }
  };

  // Manejar selección de mesas
  const handleTableSelection = async (tableId) => {
    // Verificar si la mesa está siendo añadida o quitada
    const isAdding = !newReservation.selectedTables.includes(tableId);
    
    // Verificar disponibilidad de la mesa si la estamos añadiendo
    if (isAdding && newReservation.time && newReservation.date) {
      try {
        const formattedDate = new Date(newReservation.date).toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        
        const isAvailable = await adminService.checkTableAvailability(
          tableId,
          formattedDate,
          newReservation.time,
          editingReservation ? editingReservation.id : null
        );
        
        if (!isAvailable) {
          setTableWarning(`¡La mesa ${tableId} no está disponible en este horario! Está reservada o existe un conflicto con otra reserva.`);
          return; // No continuar con la selección
        }
        
        // La mesa está disponible, continuar con la selección
        setTableWarning("");
      } catch (error) {
        console.error("Error al verificar disponibilidad:", error);
        setTableWarning("Error al verificar disponibilidad de mesa.");
        return;
      }
    }
    
    // Actualizar la selección de mesas
    setNewReservation(prev => {
      const currentTables = [...prev.selectedTables];
      
      // Si la mesa ya está seleccionada, la quitamos
      if (currentTables.includes(tableId)) {
        return {
          ...prev,
          selectedTables: currentTables.filter(id => id !== tableId)
        };
      }
      
      // Añadimos la nueva mesa a la selección
      return {
        ...prev,
        selectedTables: [...currentTables, tableId]
      };
    });
  };

  // Filtrar reservaciones por horario de comida
  const filterReservationsByMealTime = (reservations) => {
    // Consideramos que las reservas con hora anterior a las 17:00 son comidas
    // y las posteriores son cenas
    const lunchReservations = reservations.filter(reservation => {
      const hourMinutes = reservation.time.split(':');
      const hour = parseInt(hourMinutes[0]);
      return hour < 17;
    });
  
    const dinnerReservations = reservations.filter(reservation => {
      const hourMinutes = reservation.time.split(':');
      const hour = parseInt(hourMinutes[0]);
      return hour >= 17;
    });
  
    return {
      lunch: lunchReservations.sort((a, b) => a.time.localeCompare(b.time)),
      dinner: dinnerReservations.sort((a, b) => a.time.localeCompare(b.time))
    };
  };
  
  // Obtener estadísticas de reservaciones
  const getReservationStats = (reservations) => {
    const { lunch, dinner } = filterReservationsByMealTime(reservations);
    
    const lunchConfirmed = lunch.filter(r => 
      r.status === 'RESERVA CONFIRMADA' || 
      r.status === 'LLEGADA' || 
      r.status === 'SENTADA'
    );
    
    const dinnerConfirmed = dinner.filter(r => 
      r.status === 'RESERVA CONFIRMADA' || 
      r.status === 'LLEGADA' || 
      r.status === 'SENTADA'
    );
    
    return {
      lunch: {
        total: lunch.length,
        confirmed: lunchConfirmed.length,
        pending: lunch.filter(r => r.status === 'PENDIENTE DE CONFIRMACION').length,
        guests: lunchConfirmed.reduce((sum, r) => sum + (r.guests || 0), 0)
      },
      dinner: {
        total: dinner.length,
        confirmed: dinnerConfirmed.length,
        pending: dinner.filter(r => r.status === 'PENDIENTE DE CONFIRMACION').length,
        guests: dinnerConfirmed.reduce((sum, r) => sum + (r.guests || 0), 0)
      }
    };
  };
  // Manejar añadir/editar reserva
const handleAddReservation = async (e) => {
  e.preventDefault();
  try {
    // Verificar si tenemos suficiente capacidad
    if (!hasEnoughCapacity(newReservation.selectedTables, newReservation.guests, availableTables)) {
      throw new Error(`No hay suficiente capacidad para ${newReservation.guests} comensales con las mesas seleccionadas`);
    }
    
    // Verificar disponibilidad de las mesas seleccionadas
    const formattedDate = new Date(newReservation.date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    
    // Verificar cada mesa individualmente
    const tableAvailabilityPromises = newReservation.selectedTables.map(tableId =>
      adminService.checkTableAvailability(
        tableId,
        formattedDate,
        newReservation.time,
        editingReservation ? editingReservation.id : null
      )
    );
    
    const tableAvailability = await Promise.all(tableAvailabilityPromises);
    
    // Si alguna mesa no está disponible, mostrar error
    if (!tableAvailability.every(isAvailable => isAvailable)) {
      const unavailableTables = newReservation.selectedTables
        .filter((_, index) => !tableAvailability[index])
        .map(tableId => {
          const table = availableTables.find(t => t.id === tableId);
          return table ? table.name : tableId;
        })
        .join(', ');
      
      throw new Error(`Las siguientes mesas no están disponibles en este horario: ${unavailableTables}`);
    }

    const reservationData = {
      ...newReservation,
      guests: parseInt(newReservation.guests),
      tables: newReservation.selectedTables,
    };

    if (editingReservation) {
      await adminService.updateReservation(editingReservation.id, reservationData);
    } else {
      await adminService.createReservation(reservationData);
    }

    await fetchData();
    setShowAddModal(false);
    setEditingReservation(null);
    resetNewReservation();
  } catch (err) {
    setError(err.message || 'Error al gestionar la reserva');
    console.error(err);
  }
};

// Manejar cambio de estado de reserva
const handleStatusChange = async (reservationId, newStatus) => {
  try {
    // Primero obtener la reserva actual
    const reservation = reservations.find(r => r.id === reservationId);
    if (!reservation) {
      throw new Error('Reserva no encontrada');
    }

    // Actualizar el estado en la base de datos
    await adminService.updateReservationStatus(reservationId, newStatus);

    // Enviar emails solo para confirmación o cancelación de reservas normales (no walk-in)
    if ((newStatus === 'RESERVA CONFIRMADA' || newStatus === 'RESERVA CANCELADA') && 
        reservation.customer.firstName !== 'Cliente' && 
        reservation.customer.lastName !== 'Walk-in') {
      try {
        await emailService.sendReservationStatusEmail(reservation, newStatus);
      } catch (emailError) {
        console.error('Error enviando emails:', emailError);
        setError('Estado actualizado pero hubo un problema enviando las notificaciones');
      }
    }
    
    await fetchData();
  } catch (err) {
    setError('Error al actualizar el estado: ' + err.message);
    console.error(err);
  }
};

// Resetear el formulario de nueva reserva
const resetNewReservation = () => {
  setNewReservation({
    date: selectedDate,
    time: '',
    guests: '',
    customer: {
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    },
    selectedTables: [],
    status: 'PENDIENTE DE CONFIRMACION'
  });
  setEditingReservation(null);
  setTableWarning("");
  setTableSuggestions([]);
};

const handleDeleteReservation = async (reservationId) => {
  if (!window.confirm('¿Estás seguro de que deseas eliminar esta reserva? Esta acción no se puede deshacer.')) {
    return;
  }
  
  try {
    // Obtener la reserva para obtener información de las mesas
    const reservation = reservations.find(r => r.id === reservationId);
    if (!reservation) {
      throw new Error('Reserva no encontrada');
    }
    
    // Cambiar el estado a "RESERVA CANCELADA"
    // Esto liberará las mesas automáticamente usando la lógica existente
    await adminService.updateReservationStatus(reservationId, 'RESERVA CANCELADA');
    
    // Eliminar el documento
    await deleteDoc(doc(db, 'reservations', reservationId));
    
    // Actualizar la lista de reservas
    await fetchData();
    
    // Mostrar mensaje de éxito
    alert('Reserva eliminada correctamente');
  } catch (err) {
    setError('Error al eliminar la reserva: ' + err.message);
    console.error(err);
  }
};

// Colores para los diferentes estados
const getStatusColor = (status) => {
  switch (status) {
    case 'RESERVA CONFIRMADA':
      return 'bg-green-100 text-green-800';
    case 'PENDIENTE DE CONFIRMACION':
      return 'bg-yellow-100 text-yellow-800';
    case 'RESERVA CANCELADA':
      return 'bg-red-100 text-red-800';
    case 'LLEGADA':
      return 'bg-blue-100 text-blue-800';
    case 'SENTADA':
      return 'bg-purple-100 text-purple-800';
    case 'LIBERADA':
      return 'bg-gray-100 text-gray-800';
    case 'NO SHOW':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Colores para los orígenes de reserva
const getSourceColor = (source) => {
  switch(source) {
    case 'web':
      return 'bg-blue-100 text-blue-800';
    case 'google':
      return 'bg-green-100 text-green-800';
    case 'walk-in':
      return 'bg-purple-100 text-purple-800';
    case 'admin':
      return 'bg-gray-100 text-gray-800';
    case 'special':
      return 'bg-pink-100 text-pink-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Texto para mostrar el origen
const getSourceDisplay = (source) => {
  switch(source) {
    case 'web':
      return 'Web';
    case 'google':
      return 'Google';
    case 'walk-in':
      return 'Walk-in';
    case 'admin':
      return 'Admin';
    case 'special':
      return 'grupo';
    default:
      return source ? source.toUpperCase() : 'Desconocido';
  }
};
// Mostrar indicador de carga
if (loading) {
  return (
    <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>
  );
}

return (
  <div className="p-6">
    {error && (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative">
        <span className="block sm:inline">{error}</span>
        <button 
          className="absolute top-0 bottom-0 right-0 px-4 py-3"
          onClick={() => setError(null)}
        >
          <span className="text-xl">&times;</span>
        </button>
      </div>
    )}

    {/* Pestañas de administración */}
    <div className="mb-6 border-b border-gray-200">
      <nav className="-mb-px flex space-x-8">
        <button
          onClick={() => setActiveAdminTab('reservations')}
          className={`py-2 px-1 border-b-2 font-medium text-sm ${
            activeAdminTab === 'reservations'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Gestión de Reservas
        </button>
        <button
          onClick={() => setActiveAdminTab('schedule')}
          className={`py-2 px-1 border-b-2 font-medium text-sm ${
            activeAdminTab === 'schedule'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Gestión de Horarios
        </button>
      </nav>
    </div>

    {/* Contenido según la pestaña activa */}
    {activeAdminTab === 'schedule' ? (
      <ScheduleExceptions />
    ) : (
      <>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold">Reservas</h1>
          <div className="flex flex-wrap gap-4 w-full sm:w-auto">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-4 py-2 border rounded flex-grow sm:flex-grow-0"
            />
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => {
                  resetNewReservation();
                  setShowAddModal(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex-1 sm:flex-auto"
              >
                <Plus size={20} />
                <span className="hidden sm:inline">Nueva Reserva</span>
                <span className="sm:hidden">Nueva</span>
              </button>
              <button
                onClick={() => {
                  setOccupyData({
                    time: new Date().toTimeString().substring(0, 5),
                    guests: '',
                    selectedTables: [],
                    status: 'SENTADA'
                  });
                  setShowOccupyModal(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex-1 sm:flex-auto"
              >
                <Table size={20} />
                <span>Walk-In</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mb-6 hidden lg:grid lg:grid-cols-2 gap-6">
          {/* Estadísticas de Comidas - Solo visibles en pantallas grandes (lg) */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-2">Comidas</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {getReservationStats(reservations).lunch.total}
                </div>
                <div className="text-sm text-gray-500">Total Reservas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {getReservationStats(reservations).lunch.confirmed}
                </div>
                <div className="text-sm text-gray-500">Confirmadas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {getReservationStats(reservations).lunch.guests}
                </div>
                <div className="text-sm text-gray-500">Comensales</div>
              </div>
            </div>
          </div>

          {/* Estadísticas de Cenas - Solo visibles en pantallas grandes (lg) */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-2">Cenas</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {getReservationStats(reservations).dinner.total}
                </div>
                <div className="text-sm text-gray-500">Total Reservas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {getReservationStats(reservations).dinner.confirmed}
                </div>
                <div className="text-sm text-gray-500">Confirmadas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {getReservationStats(reservations).dinner.guests}
                </div>
                <div className="text-sm text-gray-500">Comensales</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Pestañas para filtrar por horarios */}
          <div className="bg-gray-100 px-2 sm:px-4 py-2 flex flex-wrap border-b">
            <button
              className={`px-2 sm:px-4 py-2 font-medium rounded-t-lg text-sm sm:text-base ${
                activeTab === 'all' 
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => setActiveTab('all')}
            >
              Todas
            </button>
            <button
              className={`px-2 sm:px-4 py-2 font-medium rounded-t-lg text-sm sm:text-base ${
                activeTab === 'lunch' 
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => setActiveTab('lunch')}
            >
              Comidas
            </button>
            <button
              className={`px-2 sm:px-4 py-2 font-medium rounded-t-lg text-sm sm:text-base ${
                activeTab === 'dinner' 
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => setActiveTab('dinner')}
            >
              Cenas
            </button>
          </div>

          {/* Tabla de reservas */}
          <table className="min-w-full divide-y divide-gray-200 text-sm sm:text-base">
            <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hora
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cliente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mesa
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comensales
              </th>
              <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comentarios
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Promo
              </th>
              {/* Columna de origen solo visible en pantallas grandes */}
              <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Origen
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(() => {
                // Filtramos las reservas según la pestaña activa
                let displayReservations = reservations;
                
                if (activeTab === 'lunch' || activeTab === 'dinner') {
                  const { lunch, dinner } = filterReservationsByMealTime(reservations);
                  displayReservations = activeTab === 'lunch' ? lunch : dinner;
                }
                
                if (displayReservations.length === 0) {
                  return (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                        No hay reservas {activeTab === 'lunch' ? 'para comidas' : activeTab === 'dinner' ? 'para cenas' : ''} en este día
                      </td>
                    </tr>
                  );
                }
                
                return displayReservations.map((reservation) => (
                  <tr key={reservation.id}>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      {reservation.time}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {reservation.customer.firstName} {reservation.customer.lastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {reservation.customer.phone}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      {Array.isArray(reservation.tables)
                        ? reservation.tables.map(tableId => {
                            const table = availableTables.find(t => t.id === tableId);
                            return table ? table.name : tableId;
                          }).join(', ')
                        : reservation.tableId || 'No asignada'}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      {reservation.guests}
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 max-w-xs">
                      {reservation.customer.comments ? (
                        <button
                          onClick={() => openCommentsModal(reservation)}
                          className="text-sm text-blue-600 hover:text-blue-800 text-left w-full group"
                          title="Click para ver comentario completo"
                        >
                          <div className="truncate group-hover:text-blue-900">
                            {reservation.customer.comments.length > 40 
                              ? `${reservation.customer.comments.substring(0, 40)}...` 
                              : reservation.customer.comments
                            }
                          </div>
                          <div className="text-xs text-blue-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            Ver completo →
                          </div>
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm italic">Sin comentarios</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(reservation.status)}`}>
                        {reservation.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                      {reservation.codigo_promo ? (
                        <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                          {reservation.codigo_promo}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded ${getSourceColor(reservation.source)}`}>
                        {getSourceDisplay(reservation.source)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(reservation)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Editar
                        </button>
                        {/* Botón de eliminar (aparece siempre) */}
                        <button
                          onClick={() => handleDeleteReservation(reservation.id)}
                          className="text-red-600 hover:text-red-900 ml-2"
                        >
                          <span className="hidden sm:inline">Eliminar</span>
                          <span className="sm:hidden">🗑️</span>
                        </button>
                        
                        {/* Acciones según el estado */}
                        {reservation.status === 'PENDIENTE DE CONFIRMACION' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(reservation.id, 'RESERVA CONFIRMADA')}
                              className="text-green-600 hover:text-green-900"
                            >
                              <span className="hidden sm:inline">Confirmar</span>
                              <span className="sm:hidden">✓</span>
                            </button>
                            <button
                              onClick={() => handleStatusChange(reservation.id, 'RESERVA CANCELADA')}
                              className="text-red-600 hover:text-red-900"
                            >
                              <span className="hidden sm:inline">Cancelar</span>
                              <span className="sm:hidden">✗</span>
                            </button>
                            <button
                              onClick={() => handleStatusChange(reservation.id, 'NO SHOW')}
                              className="text-orange-600 hover:text-orange-900"
                            >
                              <span className="hidden sm:inline">No Show</span>
                              <span className="sm:hidden">NS</span>
                            </button>
                          </>
                        )}
                        
                        {reservation.status === 'RESERVA CONFIRMADA' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(reservation.id, 'LLEGADA')}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <span className="hidden sm:inline">Llegada</span>
                              <span className="sm:hidden">🛬</span>
                            </button>
                            <button
                              onClick={() => handleStatusChange(reservation.id, 'RESERVA CANCELADA')}
                              className="text-red-600 hover:text-red-900"
                            >
                              <span className="hidden sm:inline">Cancelar</span>
                              <span className="sm:hidden">✗</span>
                            </button>
                            <button
                              onClick={() => handleStatusChange(reservation.id, 'NO SHOW')}
                              className="text-orange-600 hover:text-orange-900"
                            >
                              <span className="hidden sm:inline">No Show</span>
                              <span className="sm:hidden">NS</span>
                            </button>
                          </>
                        )}

                        {reservation.status === 'LLEGADA' && (
                          <button
                            onClick={() => handleStatusChange(reservation.id, 'SENTADA')}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            <span className="hidden sm:inline">Sentar</span>
                            <span className="sm:hidden">🪑</span>
                          </button>
                        )}

                        {reservation.status === 'SENTADA' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(reservation.id, 'LIBERADA')}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <span className="hidden sm:inline">Liberar Mesa</span>
                              <span className="sm:hidden">↪</span>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
        {/* Modal para añadir/editar reserva */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-xl my-8">
              <h2 className="text-xl font-bold mb-4">
                {editingReservation ? `Editar Reserva` : 'Nueva Reserva'}
              </h2>
              <form onSubmit={handleAddReservation}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fecha</label>
                    <input
                      type="date"
                      value={newReservation.date}
                      onChange={(e) => setNewReservation({...newReservation, date: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Hora</label>
                    <input
                      type="time"
                      value={newReservation.time}
                      onChange={handleTimeChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={newReservation.customer.firstName}
                      onChange={(e) => setNewReservation({
                        ...newReservation, 
                        customer: {...newReservation.customer, firstName: e.target.value}
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Apellidos</label>
                    <input
                      type="text"
                      value={newReservation.customer.lastName}
                      onChange={(e) => setNewReservation({
                        ...newReservation, 
                        customer: {...newReservation.customer, lastName: e.target.value}
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={newReservation.customer.email}
                      onChange={(e) => setNewReservation({
                        ...newReservation, 
                        customer: {...newReservation.customer, email: e.target.value}
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                    <input
                      type="tel"
                      value={newReservation.customer.phone}
                      onChange={(e) => setNewReservation({
                        ...newReservation, 
                        customer: {...newReservation.customer, phone: e.target.value}
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Comensales
                    </label>
                    <input
                      type="number"
                      value={newReservation.guests}
                      onChange={handleGuestsChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      min="1"
                      required
                    />
                    {newReservation.guests && (
                      <p className="mt-1 text-sm text-gray-500">
                        Necesita capacidad para {newReservation.guests} comensales
                      </p>
                    )}
                  </div>
                </div>

                {/* Mostrar alerta si hay problemas con las mesas */}
                {tableWarning && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start">
                    <AlertTriangle size={20} className="text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-yellow-800">{tableWarning}</span>
                  </div>
                )}

                {newReservation.guests && newReservation.time && (
                  <div className="col-span-2 mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Selección de Mesas
                      </label>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        hasEnoughCapacity(newReservation.selectedTables, newReservation.guests, availableTables) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {newReservation.selectedTables.length > 0 ? (
                          <>
                            Capacidad: {newReservation.selectedTables.reduce((sum, tableId) => {
                              const table = availableTables.find(t => t.id === tableId);
                              return sum + (table ? (table.capacity || 3) : 0);
                            }, 0)} / {newReservation.guests} comensales
                          </>
                        ) : (
                          'Selecciona mesas suficientes'
                        )}
                      </span>
                    </div>
                    
                    <div className="mt-2 space-y-4 max-h-64 overflow-y-auto">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Mesas Públicas</h4>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                          {getGroupedTables(newReservation.time).publico.map(table => (
                            <button
                              key={table.id}
                              type="button"
                              onClick={() => handleTableSelection(table.id)}
                              className={`p-2 text-sm rounded border ${
                                newReservation.selectedTables.includes(table.id)
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {table.name} ({table.capacity || 3} pax)
                            </button>
                          ))}
                          {getGroupedTables(newReservation.time).publico.length === 0 && (
                            <p className="col-span-3 text-sm text-gray-500 italic mt-1">No hay mesas públicas disponibles en este horario</p>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Mesas Temporales</h4>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                          {getGroupedTables(newReservation.time).temporal.map(table => (
                            <button
                              key={table.id}
                              type="button"
                              onClick={() => handleTableSelection(table.id)}
                              className={`p-2 text-sm rounded border ${
                                newReservation.selectedTables.includes(table.id)
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {table.name} ({table.capacity || 3} pax)
                            </button>
                          ))}
                          {getGroupedTables(newReservation.time).temporal.length === 0 && (
                            <p className="col-span-3 text-sm text-gray-500 italic mt-1">No hay mesas temporales disponibles en este horario</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetNewReservation();
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                    disabled={!hasEnoughCapacity(newReservation.selectedTables, newReservation.guests, availableTables)}
                  >
                    {editingReservation ? 'Guardar Cambios' : 'Crear Reserva'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal para ocupar mesa */}
        {showOccupyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-xl my-8">
              <h2 className="text-xl font-bold mb-4">
                Ocupar Mesa (Walk-In)
              </h2>
              <form onSubmit={handleOccupyTable}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Hora</label>
                    <input
                      type="time"
                      value={occupyData.time}
                      onChange={(e) => setOccupyData({...occupyData, time: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Comensales
                    </label>
                    <input
                      type="number"
                      value={occupyData.guests}
                      onChange={(e) => setOccupyData({...occupyData, guests: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      min="1"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Selección de Mesas
                      </label>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        hasEnoughCapacity(occupyData.selectedTables, occupyData.guests, availableTables) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {occupyData.selectedTables.length > 0 ? (
                          <>
                            Capacidad: {occupyData.selectedTables.reduce((sum, tableId) => {
                              const table = availableTables.find(t => t.id === tableId);
                              return sum + (table ? (table.capacity || 3) : 0);
                            }, 0)} / {occupyData.guests} comensales
                          </>
                        ) : (
                          'Selecciona mesas suficientes'
                        )}
                      </span>
                    </div>
                    
                    <div className="mt-2 space-y-4 max-h-64 overflow-y-auto">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Todas las Mesas</h4>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                        {availableTables.map(table => {
                          // Obtener la disponibilidad específica para la hora seleccionada
                          const isTimeSelected = occupyData.time && occupyData.time.trim() !== '';
                          let isAvailable = table.status === 'available';
                          
                          // Si se ha seleccionado un horario, verificar también la disponibilidad por hora
                          if (isTimeSelected && table.availabilityByHour) {
                            isAvailable = table.availabilityByHour[occupyData.time] !== false;
                          }
                          
                          // Luego usa isAvailable para determinar si la mesa se puede seleccionar
                          return (
                            <button
                              key={table.id}
                              type="button"
                              onClick={() => {
                                if (!isAvailable) {
                                  setOccupyTableWarning(`La mesa ${table.name} no está disponible en este horario.`);
                                  return;
                                }
                                
                                setOccupyTableWarning(""); // Limpiar advertencia
                                const isSelected = occupyData.selectedTables.includes(table.id);
                                setOccupyData({
                                  ...occupyData,
                                  selectedTables: isSelected
                                    ? occupyData.selectedTables.filter(id => id !== table.id)
                                    : [...occupyData.selectedTables, table.id]
                                });
                              }}
                              className={`p-2 text-sm rounded border ${
                                occupyData.selectedTables.includes(table.id)
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : isAvailable
                                    ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    : 'bg-red-50 text-gray-400 border-gray-200 cursor-not-allowed'
                              }`}
                            >
                              {table.name} ({table.capacity || 3} pax)
                            </button>
                          );
                        })}
                        </div>
                      </div>
                    </div>
                    {occupyTableWarning && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start">
                        <AlertTriangle size={20} className="text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-yellow-800">{occupyTableWarning}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowOccupyModal(false);
                      setOccupyData({
                        time: '',
                        guests: '',
                        selectedTables: [],
                        status: 'SENTADA'
                      });
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
                    disabled={!hasEnoughCapacity(occupyData.selectedTables, occupyData.guests, availableTables)}
                  >
                    Ocupar Mesa
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {showCommentsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  Comentarios de la reserva
                </h3>
                <button
                  onClick={() => setShowCommentsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Cliente:</strong> {selectedCustomerName}
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedComments}
                </p>
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowCommentsModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )}
  </div>
);

};

export default Reservations;